import { Button } from '@components/button';
import { AcquirerUser } from '@root/globalTypes';
import { ColumnDef } from '@tanstack/react-table';
import { PencilIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { UserStatusBadge } from './statusBadge';
import { DataTable } from '@components/dataTable';
import { DisableAcquirerUserButton } from './disableUserButton';
import { EnableAcquirerUserButton } from './enableUserButton';
import { ResendAcquirerUserInvitationButton } from './resendInvitationButton';
import { UseMutateFunction } from '@tanstack/react-query';
import { useCheckUserPermissions } from '@hooks/checkUserPermisions';
import { useTableSorting } from '@hooks/useTableSorting';
import { TableDateCell } from '@components/table/dateCell';

type AcquirerUsersTableProps = {
  data: AcquirerUser[];
  visibleColumns?: string[];
  onRowSelect: (id: string) => void;
  onDisable: UseMutateFunction<AcquirerUser, Error, string, unknown>;
  onEnable: UseMutateFunction<AcquirerUser, Error, string, unknown>;
  onResend?: UseMutateFunction<{ message: string }, Error, string, unknown>;
};

export function useAcquirerUsersTable({
  data,
  visibleColumns,
  onRowSelect,
  onDisable,
  onEnable,
  onResend,
}: AcquirerUsersTableProps) {
  const { t } = useTranslation('app');
  const { verifyUserPermission } = useCheckUserPermissions();
  const { sorting, updateSorting } = useTableSorting();

  const columns: ColumnDef<AcquirerUser>[] = [
    {
      id: 'name',
      accessorKey: 'name',
      header: t('table.name'),
      sortDescFirst: true,
      enableSorting: true,
    },
    {
      id: 'email',
      accessorKey: 'email',
      header: t('table.email'),
      sortDescFirst: true,
      enableSorting: true,
    },
    {
      id: 'description',
      accessorKey: 'description',
      header: t('table.description'),
      enableSorting: false,
    },
    {
      id: 'status',
      accessorKey: 'status',
      header: t('table.status'),
      enableSorting: false,
      cell: ({ row }) => <UserStatusBadge active={row.original.active} />,
    },
    {
      id: 'created_at',
      accessorKey: 'createdAt',
      header: t('table.createdAt'),
      sortDescFirst: true,
      enableSorting: true,
      cell: ({ row }) => <TableDateCell date={row.original.createdAt} />,
    },
    {
      id: 'updated_at',
      accessorKey: 'updatedAt',
      header: t('table.updatedAt'),
      sortDescFirst: true,
      enableSorting: true,
      cell: ({ row }) => <TableDateCell date={row.original.updatedAt} />,
    },
    {
      id: 'actions',
      accessorKey: 'actions',
      header: t('table.actions'),
      enableSorting: false,
      cell: ({ row }) => {
        const rowUser = row.original;
        return (
          <div className="flex items-center gap-2">
            {verifyUserPermission('acquirer_users_read') && (
              <Button
                variant="primary"
                size="sm"
                className="w-14"
                onClick={() => onRowSelect(rowUser.id)}>
                <PencilIcon className="h-5" />
              </Button>
            )}
            <EnableAcquirerUserButton user={rowUser} onEnable={onEnable} />
            <DisableAcquirerUserButton user={rowUser} onDisable={onDisable} />
            {onResend && <ResendAcquirerUserInvitationButton user={rowUser} onResend={onResend} />}
          </div>
        );
      },
    },
  ];

  return {
    columns,
    Table: () => {
      return (
        <DataTable
          columns={
            visibleColumns
              ? columns.filter(({ id }) =>
                  typeof id === 'string' ? visibleColumns.includes(id) : false,
                )
              : columns
          }
          sorting={sorting}
          onSortChange={updateSorting}
          data={data}
        />
      );
    },
  };
}
